// Tenantra Inc. - Rental income statement: pick a property (or all) and a month, quarter, year or custom dates, preview totals, download the PDF
import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { money } from './payments';

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const lastDay = (y, m) => new Date(y, m, 0).getDate();
const NOW = new Date();
const MONTHS = Array.from({ length: 24 }, (_, i) => { const d = new Date(NOW.getFullYear(), NOW.getMonth() - i, 1); return { key: iso(d).slice(0, 7), label: d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }) }; });
const QUARTERS = Array.from({ length: 8 }, (_, i) => {
  const q0 = Math.floor(NOW.getMonth() / 3) - i; const y = NOW.getFullYear() + Math.floor(q0 / 4); const q = ((q0 % 4) + 4) % 4;
  return { key: `${y}-${q}`, y, q, label: `Q${q + 1} ${y} (${['Jan to Mar', 'Apr to Jun', 'Jul to Sep', 'Oct to Dec'][q]})` };
});
const YEARS = Array.from({ length: 5 }, (_, i) => NOW.getFullYear() - i);
const KINDS = [['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year'], ['custom', 'Custom']];
const sel = 'h-10 w-full px-3 text-sm bg-white border border-slate-200 rounded-md text-slate-700';

function period(kind, v) {
  if (kind === 'month') { const [y, m] = v.month.split('-').map(Number); return [`${v.month}-01`, `${v.month}-${pad(lastDay(y, m))}`]; }
  if (kind === 'quarter') { const q = QUARTERS.find(x => x.key === v.quarter); const m = q.q * 3 + 3; return [`${q.y}-${pad(m - 2)}-01`, `${q.y}-${pad(m)}-${pad(lastDay(q.y, m))}`]; }
  if (kind === 'year') return [`${v.year}-01-01`, `${v.year}-12-31`];
  return [v.from, v.to];
}

function DateField({ label, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const d = value ? new Date(`${value}T12:00:00`) : undefined;
  return (
    <div><Label className="text-[11px] text-slate-500">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="mt-1 w-full h-10 px-3 text-left text-sm border border-slate-200 rounded-md bg-white text-slate-700 inline-flex items-center justify-between" data-testid={testId}>
            {d ? d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Select'}<CalendarDays className="w-4 h-4 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar mode="single" selected={d} defaultMonth={d} captionLayout="dropdown-buttons" fromYear={NOW.getFullYear() - 6} toYear={NOW.getFullYear() + 1}
            onSelect={(x) => { if (x) { onChange(iso(x)); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function PeriodPicker({ kind, v, set }) {
  if (kind === 'month') return <select value={v.month} onChange={(e) => set({ month: e.target.value })} className={sel} data-testid="statement-month">{MONTHS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}</select>;
  if (kind === 'quarter') return <select value={v.quarter} onChange={(e) => set({ quarter: e.target.value })} className={sel} data-testid="statement-quarter">{QUARTERS.map(q => <option key={q.key} value={q.key}>{q.label}</option>)}</select>;
  if (kind === 'year') return <select value={v.year} onChange={(e) => set({ year: e.target.value })} className={sel} data-testid="statement-year">{YEARS.map(y => <option key={y} value={y}>{y}{y === NOW.getFullYear() ? ' (year to date)' : ''}</option>)}</select>;
  return (
    <div className="grid grid-cols-2 gap-2">
      <DateField label="From" value={v.from} onChange={(x) => set({ from: x })} testId="statement-from" />
      <DateField label="To" value={v.to} onChange={(x) => set({ to: x })} testId="statement-to" />
    </div>
  );
}

function Preview({ p, error }) {
  if (error) return <p className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2" data-testid="statement-error">{error}</p>;
  if (!p) return <div className="h-[92px] bg-slate-100 rounded-lg animate-pulse" />;
  const s = p.summary;
  return (
    <div className="rounded-lg bg-slate-50 px-4 py-3 space-y-2" data-testid="statement-preview">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {[['Total income', s.total_income, 'total'], ['Rent income', s.rent_income, 'rent'], ['Other income', s.other_income, 'other'], ['Outstanding', s.outstanding, 'outstanding']].map(([l, v, k]) => (
          <div key={k}><p className="text-[11px] text-slate-500">{l}</p><p className="text-sm font-semibold text-slate-800 tabular-nums" data-testid={`statement-${k}`}>{money(v)}</p></div>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">
        {p.counts.ledger} payment{p.counts.ledger === 1 ? '' : 's'} through Tenantra · {p.counts.rent_roll} lease{p.counts.rent_roll === 1 ? '' : 's'}
        {p.t776 && <span data-testid="statement-t776"> · includes a T776 summary page</span>}
        {s.unrecorded_months > 0 && <span className="block text-amber-700 mt-0.5" data-testid="statement-unrecorded">{s.unrecorded_months} rent month{s.unrecorded_months === 1 ? ' has' : 's have'} no payment or monthly report yet.</span>}
      </p>
    </div>
  );
}

export function StatementDialog({ open, onClose, propertyId, properties }) {
  const [list, setList] = useState(properties || null);
  const [prop, setProp] = useState(propertyId || '');
  const [kind, setKind] = useState('month');
  const [v, setV] = useState({ month: MONTHS[1].key, quarter: QUARTERS[1].key, year: String(NOW.getFullYear() - 1), from: `${NOW.getFullYear()}-01-01`, to: iso(NOW) });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [start, end] = useMemo(() => period(kind, v), [kind, v]);
  const params = useMemo(() => ({ start, end, ...(prop ? { property_id: prop } : {}) }), [start, end, prop]);
  useEffect(() => { if (open && !properties) api.get('/properties', { params: { limit: 500 } }).then(r => setList(r.data.items || [])).catch(() => setList([])); }, [open, properties]);
  useEffect(() => { if (open) setProp(propertyId || ''); }, [open, propertyId]);
  useEffect(() => {
    if (!open || !start || !end) return undefined;
    setPreview(null); setError('');
    const t = setTimeout(() => api.get('/landlord/statements', { params }).then(r => setPreview(r.data)).catch(e => setError(apiHelpers.handleError(e))), 250);
    return () => clearTimeout(t);
  }, [open, params]); // eslint-disable-line
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get('/landlord/statements/pdf', { params, responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `tenantra-statement-${start}-to-${end}.pdf`;
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Statement downloaded');
    } catch { toast.error('The statement could not be created. Please try again.'); } finally { setBusy(false); }
  };
  const label = (p) => `${p.address.unit_number ? `Unit ${p.address.unit_number}, ` : ''}${p.address.street}, ${p.address.city}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle>Rental income statement</DialogTitle>
          <DialogDescription>Rent, other income, payouts and what's outstanding, as a PDF for your records or your accountant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Property</Label>
            <select value={prop} onChange={(e) => setProp(e.target.value)} disabled={!!propertyId} className={`${sel} mt-1 disabled:bg-slate-50`} data-testid="statement-property">
              {!propertyId && <option value="">All properties</option>}
              {(list || []).filter(p => !propertyId || p.id === propertyId).map(p => <option key={p.id} value={p.id}>{label(p)}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Period</Label>
            <div className="mt-1 mb-2 inline-flex rounded-full bg-slate-100/80 p-0.5 text-xs" data-testid="statement-kind">
              {KINDS.map(([k, l]) => <button key={k} type="button" onClick={() => setKind(k)} className={`h-7 px-3 rounded-full transition-colors ${kind === k ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-testid={`statement-kind-${k}`}>{l}</button>)}
            </div>
            <PeriodPicker kind={kind} v={v} set={(x) => setV(s => ({ ...s, ...x }))} />
          </div>
          <Preview p={preview} error={error} />
          <button onClick={download} disabled={busy || !!error || !preview} className="w-full h-10 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-sm font-medium disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="statement-download">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
